<script setup lang="ts">
import { computed, ref } from 'vue';
import { Card } from '@/components/ui/card';
import CoverArtTile from '@/components/CoverArtTile.vue';
import { LazyTooltip } from '@/components/ui/tooltip';
import SearchHighlight from '@/components/search/SearchHighlight.vue';
import { useQuickSearchMatches } from '@/composables/quickSearchMatches';
import { LIST_HIGHLIGHT } from '@/utils/highlightMatch';

/** The fields this card draws itself that a quick search can match. */
type LiveTrackCardMatchedField = 'title' | 'artist' | 'album' | 'copyright';

/**
 * Shared "live track preview card" surface.
 *
 * This is the one component the three full-size track-preview cards now build
 * on: the Playlists report card ({@link file:src/components/TrackCard.vue}),
 * the audio-file-issues list card ({@link
 * file:src/components/AudioFileIssues/AudioFileIssueCard.vue}), and the header
 * card of the audio-file-issue inspector ({@link
 * file:src/components/AudioFileIssueInspector.vue}). Each of those used to hand-
 * roll the same skeleton — a container-query (`@container` + `@xs:flex-row`)
 * card with a cover column on the left (cover art with ©year + duration pinned
 * beneath it) and a metadata column on the right (title / artist / album, a
 * badge row, then a bottom mono bitrate / ISRC / dates row) — so they now share
 * it here instead.
 *
 * The pieces that are always present (cover, ©year, duration, title, artist,
 * album) are props. Everything that differs between the three surfaces is a
 * named slot so each can keep its exact rendered output and behavior:
 *
 * - `corner` — the top-right badge area (position #, code, playlist count …).
 * - `badges` — the rating / severity / genre row beneath the album.
 * - `meta` — the bottom mono bitrate / ISRC / dates row.
 * - `footer` — an optional actions footer rendered inside the card, below the
 *   body (the inspector's Play MP3 / Play HLS / Re-verify row).
 *
 * The title renders as a plain `<h2>`/`<h3>` heading by default. When
 * `titleAsButton` is set it renders as a `<button>` that emits `title-click`
 * (TrackCard's "click the title to play the track").
 */
const props = withDefaults(
  defineProps<{
    coverUrl?: string | null;
    /**
     * Render as a plain flat surface (no Card border/background) instead of a
     * standalone <Card>. For embedding inside a surface that already provides
     * card chrome — e.g. the search cards view's SearchResultListRow, whose
     * fixed LiveTrack header nests this component inside the row's own Card.
     */
    flat?: boolean;
    /**
     * Whether the card stacks (cover above metadata) in narrow containers via
     * the @container query. The search cards view passes false: the cover
     * stays left and the metadata column truncates instead — the main
     * information never moves.
     */
    stack?: boolean;
    /**
     * Copyright year shown beneath the cover. Hidden when null, and (unless
     * `showZeroCopyright`) when 0 — the list cards treat 0 as "no copyright".
     */
    copyright?: number | null;
    /** Render a `0` copyright instead of hiding it (the inspector header shows `©0`). */
    showZeroCopyright?: boolean;
    /** Formatted duration string (e.g. `3:45`) shown at the cover baseline. */
    duration?: string | null;
    title: string;
    /** Renders the `artistFallback` when nullish (or also on empty string when `fallbackOnEmpty`). */
    artist?: string | null;
    /** Renders the `albumFallback` when nullish (or also on empty string when `fallbackOnEmpty`). */
    album?: string | null;
    artistFallback?: string;
    albumFallback?: string;
    /**
     * Whether an empty-string artist/album falls back to the placeholder. The
     * list cards use nullish semantics (`?? fallback`), so `''` renders blank
     * (`fallbackOnEmpty = false`); the inspector header uses falsy semantics
     * (`|| fallback`), so `''` renders the placeholder (`fallbackOnEmpty = true`).
     */
    fallbackOnEmpty?: boolean;
    /** Truncate the artist/album paragraphs (the list cards do; the inspector header does not). */
    metaTruncate?: boolean;
    /** Heading level for the title; `h3` for the list cards, `h2` for the inspector header. */
    titleTag?: 'h2' | 'h3';
    /** Render the title as a clickable button (emits `title-click`) instead of a plain heading. */
    titleAsButton?: boolean;
    /** Disable the title button (e.g. no playable code / playback already starting). */
    titleDisabled?: boolean;
    /** Truncate the plain-heading title (the list cards do; the inspector header does not). Ignored for the button variant, whose inner button always truncates. */
    titleTruncate?: boolean;
    /** Tooltip text for the heading or the title button, such as the full track title. */
    titleTooltip?: string;
    /** data-testid for the title button (only used when `titleAsButton`). */
    titleTestid?: string;
    /** Render the artist as a clickable button (emits `artist-click`) — e.g. open the Artist record. */
    artistAsButton?: boolean;
    /** Render the album as a clickable button (emits `album-click`). */
    albumAsButton?: boolean;
    artistTestid?: string;
    albumTestid?: string;
    /** Extra classes merged onto the outer `<Card>` (hover / active / cursor variants). */
    cardClass?: string;
    /** Classes for the body wrapper that holds the two columns (padding lives here for the inspector). */
    bodyClass?: string;
    /** Cross-axis alignment of the two columns: `start` (TrackCard) vs `stretch` (issue card / inspector). */
    rowAlign?: 'start' | 'stretch';
    /** ©year/duration baseline: TrackCard pins it directly under the cover (`pt`); the others push it to the bottom (`mt-auto`). */
    coverMetaToBottom?: boolean;
    /** Right padding on the metadata column so text clears the pinned `corner` slot on `@xs` rows. */
    metaColumnPadding?: string;
    /**
     * A bag of {@link PlayTrackButton} props. When provided, the cover becomes a
     * full-bleed play target with an expand-to-zoom button (forwarded to
     * {@link CoverArtTile}'s `play`). Omit it to render the plain click-to-zoom cover.
     */
    coverPlay?: Record<string, unknown>;
    /** Why this track would not play — forwarded to CoverArtTile as a badge on the art. */
    coverFailureReason?: string | null;
    /** `data-testid` for that badge. */
    coverFailureTestid?: string;
    /** `data-testid` for the expand-cover button (forwarded to CoverArtTile's `expandTestid`). */
    coverExpandTestid?: string;
    /**
     * The card's own fields that a quick search matched (SAU-591). Each is
     * highlighted where it is drawn, and a matched artist or album line wraps
     * instead of truncating. Only the search card passes any.
     */
    matched?: readonly LiveTrackCardMatchedField[];
    /**
     * Let the title row wrap, because it holds a quick-search match: in the
     * title or in the `titleTrailing` slot. The title drops its truncation.
     */
    wrapTitleRow?: boolean;
  }>(),
  {
    coverUrl: null,
    flat: false,
    stack: true,
    copyright: null,
    showZeroCopyright: false,
    duration: null,
    artist: null,
    album: null,
    artistFallback: 'Unknown artist',
    albumFallback: 'Unknown album',
    fallbackOnEmpty: false,
    metaTruncate: true,
    titleTag: 'h3',
    titleAsButton: false,
    titleDisabled: false,
    titleTruncate: false,
    titleTooltip: undefined,
    titleTestid: undefined,
    artistAsButton: false,
    albumAsButton: false,
    artistTestid: undefined,
    albumTestid: undefined,
    cardClass: '',
    bodyClass: '',
    rowAlign: 'start',
    coverMetaToBottom: false,
    metaColumnPadding: '',
    coverPlay: undefined,
    coverFailureReason: null,
    coverFailureTestid: undefined,
    coverExpandTestid: undefined,
    matched: () => [],
    wrapTitleRow: false,
  },
);

const emit = defineEmits<{
  (e: 'title-click'): void;
  (e: 'artist-click'): void;
  (e: 'album-click'): void;
}>();

defineSlots<{
  /** Top-right badge area (position #, code, playlist count …). */
  corner?: () => unknown;
  /**
   * Inline content trailing the title on the SAME row (e.g. the search card's
   * code + active dot). Unlike `corner` it reserves no column: the title
   * truncates beside it and the rows below flow full-width.
   */
  titleTrailing?: () => unknown;
  /** Rating / severity / genre row beneath the album. */
  badges?: () => unknown;
  /** Bottom mono bitrate / ISRC / dates row. */
  meta?: () => unknown;
  /** Optional actions footer rendered inside the card, below the body. */
  footer?: () => unknown;
}>();

const coverArtRef = ref<InstanceType<typeof CoverArtTile> | null>(null);

/**
 * Forwarded so consumers whose cover is covered by the play overlay (e.g.
 * TrackCard's expand-cover button) can open the zoom from their own button.
 */
defineExpose({
  openCoverPreview: () => coverArtRef.value?.openPreview(),
});

const showCopyright = computed(
  () => props.copyright != null && (props.showZeroCopyright || props.copyright !== 0),
);

const rowAlignClass = computed(() => {
  if (!props.stack) {
    return props.rowAlign === 'stretch' ? 'items-stretch' : 'items-start';
  }
  return props.rowAlign === 'stretch' ? '@xs:items-stretch' : '@xs:items-start';
});

const bodyLayoutClass = computed(() =>
  props.stack ? 'flex flex-col gap-3 @xs:flex-row' : 'flex flex-row gap-3',
);

const coverMetaClass = computed(() =>
  props.coverMetaToBottom
    ? 'text-muted-foreground mt-auto flex items-baseline pt-1 font-mono text-[11px] leading-none tabular-nums'
    : 'text-muted-foreground flex items-baseline pt-1 font-mono text-[11px] leading-none tabular-nums',
);

const metaColumnClass = computed(() =>
  ['flex min-w-0 flex-1 flex-col', props.metaColumnPadding].filter(Boolean).join(' '),
);

/** `''` keeps blank under nullish semantics, falls back under falsy semantics. */
function resolveMeta(value: string | null | undefined, fallback: string): string {
  if (value == null) return fallback;
  if (value === '' && props.fallbackOnEmpty) return fallback;
  return value;
}

const displayArtist = computed(() => resolveMeta(props.artist, props.artistFallback));
const displayAlbum = computed(() => resolveMeta(props.album, props.albumFallback));

const quickSearch = useQuickSearchMatches();
const highlightTerms = computed(() => quickSearch?.terms.value ?? []);
function isMatched(field: LiveTrackCardMatchedField): boolean {
  return props.matched.includes(field);
}

/** A truncating line that holds a match wraps instead, so the highlight is visible. */
function metaLineClass(base: string, field: LiveTrackCardMatchedField): string {
  if (!props.metaTruncate) return base;
  return `${base} ${isMatched(field) ? 'wrap-anywhere' : 'truncate'}`;
}
const artistClass = computed(() => metaLineClass('text-foreground text-sm leading-snug', 'artist'));
const albumClass = computed(() => metaLineClass('text-muted-foreground text-xs leading-snug', 'album'));

const titleClass = computed(() => {
  if (props.titleAsButton || !props.titleTruncate) return '';
  return props.wrapTitleRow ? 'wrap-anywhere' : 'truncate';
});
</script>

<template>
  <component
    :is="flat ? 'div' : Card"
    class="@container relative w-full gap-0"
    :class="[flat ? '' : 'border-border/80 shadow-sm', cardClass]"
  >
    <!-- Top-right badge area (consumer-supplied: position #, code, playlist count …). -->
    <slot name="corner" />

    <div :class="[bodyLayoutClass, rowAlignClass, bodyClass]">
      <!-- Cover column: cover art + ©year/duration baseline.
           The column owns every click inside it — play, expand, zoom — so it
           stops them here rather than letting them reach a host that made the
           whole card one control (the play queue does exactly that). -->
      <div class="flex w-24 shrink-0 flex-col" @click.stop>
        <CoverArtTile
          ref="coverArtRef"
          :src="coverUrl ?? null"
          :alt="`Cover art for ${title}`"
          :play="coverPlay"
          :expand-testid="coverExpandTestid"
          :failure-reason="coverFailureReason"
          :failure-testid="coverFailureTestid"
        />
        <div :class="coverMetaClass">
          <span v-if="showCopyright"
            >©<SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="isMatched('copyright')" whole>{{
              copyright
            }}</SearchHighlight></span
          >
          <span class="ml-auto">{{ duration }}</span>
        </div>
      </div>

      <!-- Metadata column: title / artist / album, badges, bottom mono row. -->
      <div :class="metaColumnClass">
        <!--
          The title, artist and album are often cut off, so each carries a
          tooltip with the full text. Lazy, because a card list renders one
          LiveTrackCard per row and these would otherwise mount three tooltips
          each.
        -->
        <div
          class="flex items-baseline justify-between gap-2"
          :class="wrapTitleRow ? 'flex-wrap' : ''"
          data-testid="live-track-card-title-row"
        >
          <LazyTooltip :enabled="!!titleTooltip" align="start">
            <component
              :is="titleTag"
              class="min-w-0 text-base font-semibold leading-tight"
              :class="titleClass"
            >
              <button
                v-if="titleAsButton"
                type="button"
                class="hover:text-primary focus-visible:text-primary block max-w-full cursor-pointer truncate text-left underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none disabled:cursor-progress"
                :disabled="titleDisabled"
                :data-testid="titleTestid"
                @click="emit('title-click')"
              >
                {{ title }}
              </button>
              <SearchHighlight v-else :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="isMatched('title')">{{
                title
              }}</SearchHighlight>
            </component>
            <template #content>{{ titleTooltip }}</template>
          </LazyTooltip>
          <slot name="titleTrailing" />
        </div>
        <!-- `inline` (not the button default inline-block) so a truncating line
             ellipsises mid-name rather than dropping the whole atomic name. -->
        <LazyTooltip :enabled="!!displayArtist" align="start">
          <p :class="artistClass">
            <button
              v-if="artistAsButton"
              type="button"
              class="hover:text-primary inline cursor-pointer underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
              :data-testid="artistTestid"
              @click="emit('artist-click')"
            >
              {{ displayArtist }}
            </button>
            <SearchHighlight v-else :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="isMatched('artist')">{{
              displayArtist
            }}</SearchHighlight>
          </p>
          <template #content>{{ displayArtist }}</template>
        </LazyTooltip>
        <LazyTooltip :enabled="!!displayAlbum" align="start">
          <p :class="albumClass">
            <button
              v-if="albumAsButton"
              type="button"
              class="hover:text-primary inline cursor-pointer underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
              :data-testid="albumTestid"
              @click="emit('album-click')"
            >
              {{ displayAlbum }}
            </button>
            <SearchHighlight v-else :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="isMatched('album')">{{
              displayAlbum
            }}</SearchHighlight>
          </p>
          <template #content>{{ displayAlbum }}</template>
        </LazyTooltip>
        <slot name="badges" />
        <slot name="meta" />
      </div>
    </div>

    <!-- Optional actions footer (inspector's Play / Re-verify row). -->
    <slot name="footer" />
  </component>
</template>
